import React, {
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useImperativeHandle,
} from "react";
import { AskUserDialog } from "../askUser";
import { createPortal } from "react-dom";
import { MediaItem } from "../../../../types/MediaItem";
import { getPromptOptionsForTool } from "../config/chatToolPromptOptions";
import { CHAT_COMPOSER_COLUMN_CLASS } from "../constants/chatLayout";
import {
  ModelOption,
  CHAT_MODEL_OPTIONS,
  SONNET_5_MODEL,
  MODEL_EXCLUSIVE_CHIP,
  isOpenAiModelId,
  recommendedModelFor,
  modelForChip,
  isHighTokenUsageModel,
  isSafetyReviewModel,
  SAFETY_REVIEW_NOTICE,
} from "../config/modelOptions";
import { authedFetch } from "../../../../auth/authedFetch";
import { CHAT_API_BASE_URL } from "../../../../services/chatService";

function assignRef<T>(ref: React.Ref<T> | undefined, value: T | null) {
  if (!ref) return;
  if (typeof ref === "function") ref(value);
  else (ref as React.MutableRefObject<T | null>).current = value;
}

/**
 * Imperative handle exposed by the composer so a parent can set its text
 * (starter prompts, tool-prompt prefill, clear-on-send) WITHOUT owning the
 * per-keystroke value. Keeping the typed value local to ChatInputArea is what
 * stops every keystroke from re-rendering the whole chat window + message list.
 */
export interface ChatComposerHandle {
  setValue: (value: string) => void;
}

interface ChatInputAreaProps {
  /** Optional seed/initial value. Live typing is owned internally, not here. */
  inputMessage?: string;
  /** Optional change notifier. NOT called on every keystroke (kept for compat). */
  onInputChange?: (value: string) => void;
  /** Imperative handle for external set/clear without per-keystroke re-renders. */
  composerControlRef?: React.Ref<ChatComposerHandle>;
  onSubmit: (message: string, images?: MediaItem[]) => void;
  onMyLibrary: () => void;
  onGallery: () => void;
  /** Opens the contact-import modal (SGL-80/81); omitted for agent types the + menu hides it for. */
  onImportContacts?: () => void;
  /** Opens the in-chat business-card capture panel (SGL-95). */
  onScanCard?: () => void;
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Google Drive attachments; shown in the + menu only when provided (feature enabled in parent). */
  onGoogleDrive?: () => void;
  googleDriveLoading?: boolean;
  /** Whether a Drive account is already connected for this customer; false/undefined shows a "log in" hint. */
  googleDriveConnected?: boolean;
  selectedImages: MediaItem[];
  onRemoveImage: (mediaId: string) => void;
  isLoading: boolean;
  showAttachmentMenu: boolean;
  onToggleAttachmentMenu: () => void;
  /** Close attachment popover without toggling (e.g. when opening tools menu). */
  onCloseAttachmentMenu?: () => void;
  attachmentMenuRef: React.RefObject<HTMLDivElement>;
  fileInputRef: React.RefObject<HTMLInputElement>;
  tools?: string[];
  onUseLogo?: () => void;
  logoLoading?: boolean;
  showQuickActions?: boolean;
  showUGCButton?: boolean;
  onUGCVideoClick?: () => void;
  isUGCVideoActive?: boolean;
  /** Always show Create Image in the + menu (defaults true). */
  showCreateImageButton?: boolean;
  /** Navigate to Video Avatar (e.g. full-page flow); shown in tools menu when set. */
  onVideoAvatarClick?: () => void;
  /** Show Reddit Feed button in + menu (content creator agents only). */
  showRedditFeedButton?: boolean;
  /** Show Quick Search / Deep Research in the + menu (defaults true; Receptionist agents pass false). */
  showResearchTools?: boolean;
  /** Called when user clicks the Reddit Feed menu item. */
  onRedditFeedClick?: () => void;
  /** A feed build is running in the background (the panel may be closed). */
  redditFeedLoading?: boolean;
  /** A background build finished and the user hasn't looked at it yet. */
  redditFeedHasUnseen?: boolean;
  /** Called when user picks a tool that has no prompt options (fallback). */
  onToolSelect?: (toolId: string) => void;
  /**
   * Plan gate for chips whose tier rules the parent owns. Returns true when
   * it handled the click (by showing an upgrade prompt), in which case the
   * chip must not activate.
   */
  interceptGatedTool?: (toolId: string) => boolean;
  /** Called when user picks a tool + prompt option; parent should prefill input and set tool for next message. */
  onToolPromptSelect?: (toolId: string, prompt: string) => void;
  /** docked: bottom bar (default). inline: sits under welcome content on new chats. */
  variant?: "docked" | "inline";
  /** Match shell background when chat is embedded (e.g. Perplexity-style home). */
  embedded?: boolean;
  /** Optional ref to the textarea (merged with internal ref for auto-resize). */
  composerTextareaRef?: React.Ref<HTMLTextAreaElement>;
  /** The Agent's type. Decides which model the model menu tags "recommended" (SGL-423). */
  agentType?: string | null;
  /**
   * The model stored on the Agent. An Agent stored on a GPT model is on OpenAI, so the menu tags
   * GPT-5.6 Terra instead of the type's Claude default: it stays on OpenAI to keep its knowledge files.
   */
  agentModel?: string | null;
  /** Currently selected model/provider. */
  selectedModel?: ModelOption;
  /** Called when user picks a different model/provider. */
  onModelChange?: (model: ModelOption) => void;
  /** The currently active chip tool ID (persists across sends). */
  activeChipId?: string | null;
  /** Dismiss the active chip entirely. */
  onChipDismiss?: () => void;
  /** Switch to a different chip (or toggle off if same). */
  onChipSelect?: (toolId: string) => void;
  /** Called when user clicks the stop button during streaming. */
  onStop?: () => void;
  /**
   * No "+" menu (files, media library, tools): for an agent that runs before a
   * Brand exists (onboarding), where those have nothing to act on. Off by default.
   */
  minimal?: boolean;
}


/** Working tools shown in the + menu — only these IDs are rendered. */
interface PlusMenuToolConfig {
  label: string;
  description: string;
  /** Tailwind bg color class for the icon badge */
  iconBg: string;
  icon: React.ReactNode;
}

const PLUS_MENU_TOOL_CONFIG: Record<string, PlusMenuToolConfig> = {
  perplexity_search: {
    label: "Quick Search",
    description: "Instant answers — best for most questions.",
    iconBg: "bg-blue-100",
    icon: (
      <svg className="h-4 w-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  deep_research: {
    label: "Deep Research",
    description: "Thorough multi-source research report. Takes 1–2 min.",
    iconBg: "bg-purple-100",
    icon: (
      <svg className="h-4 w-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
      </svg>
    ),
  },
  social_posts: {
    label: "Social Posts",
    description: "Create posts for all platforms.",
    iconBg: "bg-emerald-100",
    icon: (
      <svg className="h-4 w-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    ),
  },
  create_image: {
    label: "Create Image",
    description: "Generate images for your content.",
    iconBg: "bg-orange-100",
    icon: (
      <svg className="h-4 w-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
  },
  create_image_batch: {
    label: "Bulk Images",
    description: "A set of images on one theme — a different message on each.",
    iconBg: "bg-orange-100",
    icon: (
      <svg className="h-4 w-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4h10a2 2 0 012 2v10M4 8h10a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2v-8a2 2 0 012-2z" />
      </svg>
    ),
  },
  blog: {
    label: "Blog",
    description: "Write and publish a blog post.",
    iconBg: "bg-purple-100",
    icon: (
      <svg className="h-4 w-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>
    ),
  },
  outreach_email: {
    label: "Outreach Email",
    description: "Draft a personalized campaign email.",
    iconBg: "bg-blue-100",
    icon: (
      <svg className="h-4 w-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
  },
};

export const ChatInputArea: React.FC<ChatInputAreaProps> = ({
  inputMessage = "",
  onInputChange,
  composerControlRef,
  onSubmit,
  onMyLibrary,
  onGallery: _onGallery,
  onImportContacts,
  onScanCard,
  onFileSelect,
  onGoogleDrive,
  googleDriveLoading = false,
  googleDriveConnected = false,
  selectedImages,
  onRemoveImage,
  isLoading,
  showAttachmentMenu: _showAttachmentMenu,
  onToggleAttachmentMenu: _onToggleAttachmentMenu,
  onCloseAttachmentMenu: _onCloseAttachmentMenu,
  attachmentMenuRef: _attachmentMenuRef,
  fileInputRef,
  tools = [],
  onUseLogo,
  logoLoading = false,
  showQuickActions: _showQuickActions,
  showUGCButton = false,
  onUGCVideoClick,
  isUGCVideoActive = false,
  showCreateImageButton = true,
  onVideoAvatarClick: _onVideoAvatarClick,
  showRedditFeedButton = false,
  showResearchTools = true,
  onRedditFeedClick,
  redditFeedLoading = false,
  redditFeedHasUnseen = false,
  onToolSelect,
  interceptGatedTool,
  onToolPromptSelect,
  variant = "docked",
  embedded: _embedded,
  composerTextareaRef,
  agentType,
  agentModel,
  selectedModel,
  onModelChange,
  activeChipId = null,
  onChipDismiss,
  onChipSelect,
  onStop,
  minimal = false,
}) => {
  // The live, per-keystroke value is owned HERE, not by the parent chat window.
  // This is what prevents each keystroke from re-rendering NewChatWindow and the
  // entire message list (the cause of typing lag in long conversations).
  const [value, setValue] = useState(inputMessage);
  // The model the menu tags "recommended": the Agent type's default, or GPT-5.6 Terra for an
  // Agent that is on OpenAI (SGL-423).
  const recommendedModel = recommendedModelFor(
    agentType,
    isOpenAiModelId(agentModel) ? "openai" : undefined,
  ).model;
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const recognitionRef = useRef<any>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  // Let the parent set/clear the text imperatively (starter prompts, tool-prompt
  // prefill, clear-on-send) without re-introducing per-keystroke coupling.
  useImperativeHandle(
    composerControlRef,
    () => ({ setValue: (next: string) => setValue(next ?? "") }),
    [],
  );

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  /** The single place that decides what leaves the composer. Memoized so the
   *  two voice-transcription useCallbacks that depend on it don't pin a stale
   *  copy of selectedImages. */
  const sendMessage = useCallback(
    (text: string) => onSubmit(text, selectedImages),
    [onSubmit, selectedImages],
  );

  const setTextareaRef = useCallback(
    (el: HTMLTextAreaElement | null) => {
      textareaRef.current = el;
      assignRef(composerTextareaRef, el);
    },
    [composerTextareaRef],
  );

  const modelMenuRef = useRef<HTMLDivElement>(null);
  const modelPortalRef = useRef<HTMLDivElement>(null);
  const [showModelMenu, setShowModelMenu] = useState(false);
  const [modelMenuAnchorRect, setModelMenuAnchorRect] = useState<DOMRect | null>(null);
  // Tracks which high-token-usage model the user last dismissed the cost notice for, so it
  // stays dismissed for that model but reappears if they switch to a *different* one (e.g.
  // Sonnet 5 -> Opus 5) that they haven't seen the notice for yet.
  const [dismissedHighUsageModel, setDismissedHighUsageModel] = useState<string | null>(null);

  // Plus menu
  const plusMenuRef = useRef<HTMLDivElement>(null);
  const plusPortalRef = useRef<HTMLDivElement>(null);
  const [showPlusMenu, setShowPlusMenu] = useState(false);
  const [plusMenuAnchorRect, setPlusMenuAnchorRect] = useState<DOMRect | null>(null);
  const [plusMenuDirection, setPlusMenuDirection] = useState<"up" | "down">("up");

  const stopRecording = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const uploadAndTranscribe = useCallback(async (audioBlob: Blob, mimeType: string, existingText: string = "") => {
    setIsTranscribing(true);
    try {
      const ext = mimeType.includes("ogg") ? "ogg" : mimeType.includes("mp4") ? "mp4" : "webm";
      const formData = new FormData();
      formData.append("file", audioBlob, `audio.${ext}`);
      formData.append("language", "en");
      const response = await authedFetch(`${CHAT_API_BASE_URL}/chat/transcribe`, {
        method: "POST",
        body: formData,
      });
      if (!response.ok) throw new Error(`Transcription failed: ${response.status}`);
      const data = await response.json();
      const trimmed = (data.text || "").trim();
      if (trimmed && !isLoading) {
        const combined = existingText ? `${existingText} ${trimmed}` : trimmed;
        setValue("");
        onInputChange?.("");
        sendMessage(combined);
      }
    } catch (err) {
      console.error("Transcription error:", err);
    } finally {
      setIsTranscribing(false);
    }
  }, [onInputChange, isLoading, sendMessage]);

  const startRecording = useCallback(() => {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const existingText = value.trim();
    const prefix = existingText ? existingText + " " : "";

    if (SR) {
      // Live real-time path: Chrome, Edge
      const recognition = new SR();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = "en-US";
      let finalTranscript = "";

      recognition.onresult = (event: any) => {
        let interim = "";
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) finalTranscript += event.results[i][0].transcript;
          else interim += event.results[i][0].transcript;
        }
        const display = prefix + finalTranscript + interim;
        setValue(display);
        onInputChange?.(display);
      };

      recognition.onend = () => {
        setIsRecording(false);
        recognitionRef.current = null;
        const trimmed = finalTranscript.trim();
        if (trimmed && !isLoading) {
          const combined = (prefix + trimmed).trim();
          setValue("");
          onInputChange?.("");
          sendMessage(combined);
        }
      };

      recognition.onerror = (event: any) => {
        if (event.error !== "no-speech") console.error("Speech recognition error:", event.error);
        setIsRecording(false);
        recognitionRef.current = null;
      };

      recognition.start();
      recognitionRef.current = recognition;
      setIsRecording(true);
    } else {
      // MediaRecorder fallback: Firefox, Safari — records then uploads to Whisper
      navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
        const mimeType = MediaRecorder.isTypeSupported("audio/webm; codecs=opus")
          ? "audio/webm; codecs=opus"
          : MediaRecorder.isTypeSupported("audio/ogg; codecs=opus")
          ? "audio/ogg; codecs=opus"
          : "audio/mp4";

        const recorder = new MediaRecorder(stream, { mimeType });
        audioChunksRef.current = [];

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) audioChunksRef.current.push(e.data);
        };

        recorder.onstop = () => {
          stream.getTracks().forEach((t) => t.stop());
          setIsRecording(false);
          mediaRecorderRef.current = null;
          const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
          audioChunksRef.current = [];
          uploadAndTranscribe(audioBlob, mimeType, existingText);
        };

        recorder.start();
        mediaRecorderRef.current = recorder;
        setIsRecording(true);
      }).catch((err) => {
        console.error("Microphone access denied:", err);
      });
    }
  }, [value, onInputChange, isLoading, uploadAndTranscribe, sendMessage]);

  const closePlusMenu = useCallback(() => {
    setShowPlusMenu(false);
  }, []);

  // "Edit an existing image" source picker (My Library vs Local file).
  const [showEditImageSource, setShowEditImageSource] = useState(false);
  const editImageSourceRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showEditImageSource) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        editImageSourceRef.current &&
        !editImageSourceRef.current.contains(event.target as Node)
      ) {
        setShowEditImageSource(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showEditImageSource]);

  const workingTools = (() => {
    // Map backend tool names → chip IDs (backend uses different names than the UI chips).
    const BACKEND_TO_CHIP: Record<string, string> = {
      output_social_posts: "social_posts",
      output_blog: "blog",
      output_outreach_email: "outreach_email",
    };
    // Quick Search and Deep Research work via model switching to Perplexity —
    // available for every type except Receptionist (showResearchTools).
    const base = tools.map((t) => BACKEND_TO_CHIP[t] ?? t);
    if (showResearchTools) {
      if (!base.includes("perplexity_search")) base.unshift("perplexity_search");
      if (!base.includes("deep_research")) base.push("deep_research");
    }
    return base
      .filter((t) => PLUS_MENU_TOOL_CONFIG[t] && t !== "create_image")
      .map((t) => ({ id: t, config: PLUS_MENU_TOOL_CONFIG[t] }))
      .filter((item, idx, arr) => arr.findIndex((x) => x.config.label === item.config.label) === idx);
  })();

  // Model menu click-outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        modelMenuRef.current &&
        !modelMenuRef.current.contains(target) &&
        (!modelPortalRef.current || !modelPortalRef.current.contains(target))
      ) {
        setShowModelMenu(false);
      }
    };
    if (showModelMenu) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showModelMenu]);

  useLayoutEffect(() => {
    if (showModelMenu && modelMenuRef.current) {
      setModelMenuAnchorRect(modelMenuRef.current.getBoundingClientRect());
    } else if (!showModelMenu) {
      setModelMenuAnchorRect(null);
    }
  }, [showModelMenu]);

  // Plus menu click-outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        plusMenuRef.current &&
        !plusMenuRef.current.contains(target) &&
        (!plusPortalRef.current || !plusPortalRef.current.contains(target))
      ) {
        setShowPlusMenu(false);
      }
    };
    if (showPlusMenu) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showPlusMenu]);

  // Plus menu anchor rect + flip direction
  useLayoutEffect(() => {
    if (showPlusMenu && plusMenuRef.current) {
      const rect = plusMenuRef.current.getBoundingClientRect();
      setPlusMenuAnchorRect(rect);
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      // Only open downward if there is substantially more room below than above
      // AND enough room for a full menu. On laptops spaceBelow is often only
      // 280-350px which caps maxHeight to ~265px — not enough for all items.
      setPlusMenuDirection(spaceBelow >= 420 && spaceBelow >= spaceAbove ? "down" : "up");
    } else if (!showPlusMenu) {
      setPlusMenuAnchorRect(null);
    }
  }, [showPlusMenu]);

  /** Scanned cards ride along with the next message as plain lines the agent
   *  (and the existing /contacts/import parser) can already read. */

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if ((value.trim() || selectedImages.length > 0) && !isLoading) {
      sendMessage(value.trim());
    }
  };

  /** Grow with content; only scroll after a large cap (viewport-based) for huge pastes. */
  const adjustComposerHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const maxH =
      typeof window !== "undefined"
        ? Math.min(window.innerHeight * 0.7, 512)
        : 512;
    const contentH = el.scrollHeight;
    const next = Math.min(contentH, maxH);
    el.style.height = `${next}px`;
    el.style.overflowY = contentH > maxH ? "auto" : "hidden";
  }, []);

  useLayoutEffect(() => {
    adjustComposerHeight();
  }, [value, adjustComposerHeight]);

  useEffect(() => {
    window.addEventListener("resize", adjustComposerHeight);
    return () => window.removeEventListener("resize", adjustComposerHeight);
  }, [adjustComposerHeight]);

  // Close menus on resize; on scroll, keep plus menu open when the user scrolls inside it.
  useEffect(() => {
    if (showPlusMenu || showModelMenu) {
      const handleScrollOrResize = (event: Event) => {
        const target = event.target as Node | null;
        if (
          event.type === "scroll" &&
          target &&
          ((plusPortalRef.current && plusPortalRef.current.contains(target)) ||
            (plusMenuRef.current && plusMenuRef.current.contains(target)) ||
            (modelPortalRef.current && modelPortalRef.current.contains(target)) ||
            (modelMenuRef.current && modelMenuRef.current.contains(target)))
        ) {
          return;
        }
        setTimeout(() => {
          setShowPlusMenu(false);
          setShowModelMenu(false);
        }, 0);
      };
      window.addEventListener("scroll", handleScrollOrResize, true);
      window.addEventListener("resize", handleScrollOrResize);
      return () => {
        window.removeEventListener("scroll", handleScrollOrResize, true);
        window.removeEventListener("resize", handleScrollOrResize);
      };
    }
  }, [showPlusMenu, showModelMenu]);

  const shellBg = variant === "inline" ? "bg-transparent" : "";
  /** Perplexity-style docked bar: top edge + frosted strip */
  const shellClass =
    variant === "inline"
      ? `w-full px-0 pt-0 pb-0 bg-transparent flex-shrink-0`
      : `flex-shrink-0 bg-theme-background/90 px-4 pb-4 pt-2.5 backdrop-blur-md supports-[backdrop-filter]:bg-theme-background/75 sm:px-6 sm:pb-5 sm:pt-3 ${shellBg}`;

  const columnClass = `${CHAT_COMPOSER_COLUMN_CLASS} min-w-0`;

  return (
    <div className={shellClass}>
      <AskUserDialog disabled={isLoading} />
      <div className={`${columnClass} min-w-0`}>

        {/* Selected Files Preview (images + non-image files) */}
        {selectedImages.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-2">
            {selectedImages.map((item) => {
              const isImage =
                item.media_type === "IMAGE" ||
                (item.media_type || "").startsWith("image/") ||
                item.cloudfront_url?.startsWith("data:image");
              return isImage ? (
                <div key={item.media_id} className="relative group">
                  <img
                    src={item.cloudfront_url}
                    alt={item.s3_key}
                    className="w-20 h-20 object-cover rounded-lg border border-theme-border"
                  />
                  <button
                    type="button"
                    onClick={() => onRemoveImage(item.media_id)}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs hover:bg-red-600"
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <div key={item.media_id} className="relative group flex items-center gap-1.5 rounded-lg border border-theme-border bg-theme-surface px-2.5 py-1.5 text-sm text-theme-fg shadow-sm">
                  <svg className="h-4 w-4 shrink-0 text-theme-fg-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="max-w-[120px] truncate text-[12px]">{item.s3_key}</span>
                  <button
                    type="button"
                    onClick={() => onRemoveImage(item.media_id)}
                    className="ml-0.5 text-theme-fg-muted hover:text-red-500 transition-colors text-xs"
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Active chip badge + suggestion pills */}
        {activeChipId && (() => {
          const chipConfig = PLUS_MENU_TOOL_CONFIG[activeChipId];
          const promptOpts = getPromptOptionsForTool(activeChipId);
          if (!chipConfig) return null;
          return (
            <div className="mb-2 space-y-2">
              <div className="flex items-center gap-2">
                <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md ${chipConfig.iconBg}`}>
                  {chipConfig.icon}
                </span>
                <span className="text-xs font-medium text-theme-fg">{chipConfig.label} mode</span>
                <button
                  type="button"
                  onClick={onChipDismiss}
                  className="ml-auto flex h-5 w-5 items-center justify-center rounded-full text-theme-fg-muted transition-colors hover:bg-theme-secondary/10 hover:text-theme-fg"
                  title="Remove chip"
                >
                  <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              {promptOpts && (
                <div className="flex flex-wrap gap-1.5">
                  {(activeChipId === "create_image"
                    ? promptOpts.promptOptions
                    : promptOpts.promptOptions.slice(0, 4)
                  ).map((opt) => {
                    // "Edit an existing image" needs a source image first — let the user
                    // pick between My Library and a local file (reusing onMyLibrary + the
                    // shared file input) instead of prefilling an instructional sentence.
                    if (activeChipId === "create_image" && opt.id === "edit") {
                      return (
                        <div key={opt.id} className="relative" ref={editImageSourceRef}>
                          <button
                            type="button"
                            disabled={isLoading}
                            onClick={() => setShowEditImageSource((prev) => !prev)}
                            className="rounded-full border border-theme-border bg-theme-surface px-3 py-1 text-xs text-theme-fg transition-colors hover:border-theme-primary/30 hover:bg-theme-secondary/10 disabled:opacity-50"
                          >
                            {opt.label}
                          </button>
                          {showEditImageSource && (
                            <div className="absolute bottom-full left-0 z-50 mb-2 flex w-44 flex-col rounded-xl border border-theme-border bg-theme-surface p-1.5 shadow-xl ring-1 ring-black/5">
                              <button
                                type="button"
                                onClick={() => {
                                  setShowEditImageSource(false);
                                  onMyLibrary();
                                }}
                                className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm font-medium text-theme-fg transition-colors hover:bg-theme-secondary/10"
                              >
                                <span className="material-icons text-[18px] text-theme-fg-muted">photo_library</span>
                                My Library
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setShowEditImageSource(false);
                                  fileInputRef.current?.click();
                                }}
                                className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm font-medium text-theme-fg transition-colors hover:bg-theme-secondary/10"
                              >
                                <span className="material-icons text-[18px] text-theme-fg-muted">upload_file</span>
                                Local files
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    }
                    return (
                    <button
                      key={opt.id}
                      type="button"
                      disabled={isLoading}
                      onClick={() => {
                        // Re-assert the active chip's model: the user may have changed it
                        // since selecting the chip, and these pills are that chip's prompts.
                        const preferred = modelForChip(activeChipId);
                        if (onModelChange && preferred) onModelChange(preferred);
                        onToolPromptSelect?.(activeChipId, opt.prompt);
                      }}
                      className="rounded-full border border-theme-border bg-theme-surface px-3 py-1 text-xs text-theme-fg transition-colors hover:border-theme-primary/30 hover:bg-theme-secondary/10 disabled:opacity-50"
                    >
                      {opt.label}
                    </button>
                    );
                  })}
                  {/* Brand logo — surfaced as a pill above the chat (was the Create Image sub-panel) */}
                  {activeChipId === "create_image" && onUseLogo && (
                    <button
                      type="button"
                      disabled={isLoading || logoLoading}
                      onClick={() => { if (!logoLoading) onUseLogo(); }}
                      className="rounded-full border border-theme-border bg-theme-surface px-3 py-1 text-xs text-theme-fg transition-colors hover:border-theme-primary/30 hover:bg-theme-secondary/10 disabled:opacity-50"
                    >
                      {logoLoading ? "Loading logo…" : "Use my brand logo"}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* Input Form */}
        <form onSubmit={handleSubmit} className="relative">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.pdf,.doc,.docx,.json,.txt,.csv,.md"
            multiple
            onChange={onFileSelect}
            className="hidden"
          />
          <div
            className={`relative rounded-[28px] border border-theme-border bg-theme-surface shadow-[0_2px_12px_rgba(15,23,42,0.06)] transition-all duration-200 hover:border-theme-border/90 focus-within:border-theme-border focus-within:shadow-[0_2px_16px_rgba(15,23,42,0.08)] ${
              variant === "inline" ? "shadow-lg" : ""
            }`}
          >
            <textarea
              ref={setTextareaRef}
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                onInputChange?.(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (
                    (value.trim() || selectedImages.length > 0) &&
                    !isLoading
                  ) {
                    handleSubmit(e);
                  }
                }
              }}
              placeholder="Ask a follow-up…"
              className="chat-composer-textarea w-full resize-none overflow-x-hidden border-0 bg-transparent px-5 pb-11 pt-3.5 text-left text-[15px] leading-[1.5] text-theme-fg placeholder:text-left placeholder:text-slate-400 outline-none focus:border-0 focus:outline-none focus:ring-0 min-h-[3rem] sm:min-h-[3.25rem]"
              disabled={isLoading}
            />
            {/* Listening / processing indicator */}
            {(isRecording || isTranscribing) && (
              <div className="pointer-events-none absolute inset-x-5 bottom-[2.75rem] flex items-center gap-1.5">
                <span className={`h-[6px] w-[6px] shrink-0 rounded-full animate-pulse ${isTranscribing ? "bg-blue-500" : "bg-red-500"}`} />
                <span className="text-[11px] text-theme-fg-muted">
                  {isTranscribing ? "Processing audio…" : "Listening… tap stop when done"}
                </span>
              </div>
            )}
            {/* Unified + button — replaces separate clip and wrench icons */}
            <div className="absolute bottom-2 left-3 flex items-center gap-1 sm:bottom-2.5 sm:left-3.5">
            {!minimal && (
            <div className="relative" ref={plusMenuRef}>
              <button
                type="button"
                onClick={() => setShowPlusMenu((prev) => !prev)}
                className={`relative flex h-8 w-8 items-center justify-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-theme-secondary/40 focus:ring-offset-0 ${
                  showPlusMenu
                    ? "bg-theme-secondary/20 text-theme-primary"
                    : "text-theme-fg-muted hover:bg-theme-secondary/10"
                }`}
                title={
                  showRedditFeedButton && redditFeedHasUnseen
                    ? "New Reddit posts ready"
                    : showRedditFeedButton && redditFeedLoading
                    ? "Finding Reddit posts…"
                    : "Add files or choose a task"
                }
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {/* Surfaced on the button, not just the menu item inside it —
                    a signal you have to open a menu to see isn't a signal.
                    Gated on the feature existing here: the feed state is
                    app-wide and may belong to another agent entirely. */}
                {showRedditFeedButton && redditFeedHasUnseen && !redditFeedLoading && (
                  <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-green-500 ring-2 ring-theme-background" />
                )}
              </button>

              {showPlusMenu && plusMenuAnchorRect && createPortal(
                <div
                  ref={plusPortalRef}
                  className="fixed z-[9999] w-[280px] overflow-y-auto rounded-2xl border border-theme-border bg-theme-surface shadow-xl"
                  style={{
                    ...(plusMenuDirection === "down"
                      ? { top: `${plusMenuAnchorRect.bottom + 8}px` }
                      : { bottom: `${window.innerHeight - plusMenuAnchorRect.top + 8}px` }),
                    left: `${plusMenuAnchorRect.left}px`,
                    maxHeight: `min(48vh, ${(plusMenuDirection === "down" ? window.innerHeight - plusMenuAnchorRect.bottom - 16 : plusMenuAnchorRect.top - 16) * 0.6}px)`,
                  }}
                >
                  {(
                    <>
                      {/* ── Files section ── */}
                      <div className="px-4 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-widest text-theme-fg-muted">
                        Files
                      </div>

                      {/* Files from computer */}
                      <button
                        type="button"
                        onClick={() => { fileInputRef.current?.click(); closePlusMenu(); }}
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-theme-secondary/10"
                      >
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-slate-100">
                          <svg className="h-4 w-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                          </svg>
                        </span>
                        <div>
                          <p className="text-sm font-medium text-theme-fg">Files from computer</p>
                          <p className="text-[11px] text-theme-fg-muted">Images, PDFs, DOCX, JSON and more.</p>
                        </div>
                      </button>
                      {/* Scan Business Card (SGL-95) */}
                      {onScanCard && (
                        <button
                          type="button"
                          onClick={() => { onScanCard(); closePlusMenu(); }}
                          className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-theme-secondary/10"
                        >
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-amber-100">
                            <svg className="h-4 w-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </span>
                          <div>
                            <p className="text-sm font-medium text-theme-fg">Scan Business Card</p>
                            <p className="text-[11px] text-theme-fg-muted">Photograph a card — we read the details for you.</p>
                          </div>
                        </button>
                      )}

                      {/* My Library */}
                      <button
                        type="button"
                        onClick={() => { onMyLibrary(); closePlusMenu(); }}
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-theme-secondary/10"
                      >
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-sky-100">
                          <svg className="h-4 w-4 text-sky-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </span>
                        <div>
                          <p className="text-sm font-medium text-theme-fg">My Library</p>
                          <p className="text-[11px] text-theme-fg-muted">Pick from your media library.</p>
                        </div>
                      </button>

                      {/* Import Contacts (SGL-80/81) */}
                      {onImportContacts && (
                        <button
                          type="button"
                          onClick={() => { onImportContacts(); closePlusMenu(); }}
                          className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-theme-secondary/10"
                        >
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-emerald-100">
                            <svg className="h-4 w-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-6.13a4 4 0 11-8 0 4 4 0 018 0zm6 3a4 4 0 10-8 0 4 4 0 008 0z" />
                            </svg>
                          </span>
                          <div>
                            <p className="text-sm font-medium text-theme-fg">Import Contacts</p>
                            <p className="text-[11px] text-theme-fg-muted">Paste or CSV — review before it goes to GHL.</p>
                          </div>
                        </button>
                      )}


                      {/* Google Drive */}
                      {onGoogleDrive && (
                        <button
                          type="button"
                          onClick={() => {
                            if (googleDriveLoading) return;
                            onGoogleDrive();
                            closePlusMenu();
                          }}
                          disabled={googleDriveLoading}
                          className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-theme-secondary/10 disabled:cursor-not-allowed disabled:opacity-60 ${
                            !googleDriveConnected && !googleDriveLoading ? "opacity-70" : ""
                          }`}
                        >
                          <span
                            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl ${
                              googleDriveConnected || googleDriveLoading ? "bg-emerald-100" : "bg-slate-100"
                            }`}
                          >
                            {googleDriveLoading ? (
                              <div className="h-4 w-4 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" />
                            ) : (
                              <svg
                                className={`h-4 w-4 ${googleDriveConnected ? "text-emerald-600" : "text-slate-400"}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                aria-hidden
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1M8 12h8m-4-4v8" />
                              </svg>
                            )}
                          </span>
                          <div>
                            <p className="text-sm font-medium text-theme-fg">Google Drive</p>
                            <p className={`text-[11px] ${!googleDriveConnected && !googleDriveLoading ? "text-theme-fg-muted font-medium" : "text-theme-fg-muted"}`}>
                              {googleDriveLoading
                                ? "Connecting…"
                                : googleDriveConnected
                                  ? "Attach a file from Drive."
                                  : "Not connected — log in with Google."}
                            </p>
                          </div>
                        </button>
                      )}

                      {/* ── Create with AI section ── */}
                      {(showCreateImageButton || workingTools.length > 0 || (showUGCButton && onUGCVideoClick)) && (
                        <>
                          <div className="mx-4 my-2 border-t border-theme-border" />
                          <div className="px-4 pb-1 text-[10px] font-semibold uppercase tracking-widest text-theme-fg-muted">
                            Create with AI
                          </div>

                          {/* Create Image — always shown */}
                          {showCreateImageButton && (() => {
                            const config = PLUS_MENU_TOOL_CONFIG["create_image"];
                            return (
                              <button
                                type="button"
                                onClick={() => { if (selectedModel?.provider === "perplexity") onModelChange?.(SONNET_5_MODEL); onChipSelect?.("create_image"); closePlusMenu(); }}
                                className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-theme-secondary/10"
                              >
                                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl ${config.iconBg}`}>
                                  {config.icon}
                                </span>
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium text-theme-fg">{config.label}</p>
                                  <p className="text-[11px] text-theme-fg-muted">{config.description}</p>
                                </div>
                              </button>
                            );
                          })()}

                          {/* Bulk Images (SGL-181) — shares the Create-Image visibility
                              gate (content agents only). */}
                          {showCreateImageButton
                            && (() => {
                            const config = PLUS_MENU_TOOL_CONFIG["create_image_batch"];
                            return (
                              <button
                                type="button"
                                onClick={() => { if (selectedModel?.provider === "perplexity") onModelChange?.(SONNET_5_MODEL); onChipSelect?.("create_image_batch"); closePlusMenu(); }}
                                className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-theme-secondary/10"
                              >
                                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl ${config.iconBg}`}>
                                  {config.icon}
                                </span>
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium text-theme-fg">{config.label}</p>
                                  <p className="text-[11px] text-theme-fg-muted">{config.description}</p>
                                </div>
                              </button>
                            );
                          })()}

                          {/* Create Video (UGC) — placed before research tools so it's always in view */}
                          {showUGCButton && onUGCVideoClick && (
                            <button
                              type="button"
                              onClick={() => { if (!isUGCVideoActive) { onUGCVideoClick(); closePlusMenu(); } }}
                              disabled={isUGCVideoActive}
                              className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-theme-secondary/10 disabled:opacity-50"
                            >
                              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-red-100">
                                <svg className="h-4 w-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                              </span>
                              <div>
                                <p className="text-sm font-medium text-theme-fg">
                                  Create Video{isUGCVideoActive ? " (open)" : ""}
                                </p>
                                <p className="text-[11px] text-theme-fg-muted">Generate videos for your content.</p>
                              </div>
                            </button>
                          )}

                          {/* Reddit Feed — content creator agents only */}
                          {showRedditFeedButton && onRedditFeedClick && (
                            <button
                              type="button"
                              onClick={() => { onRedditFeedClick(); closePlusMenu(); }}
                              className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-theme-secondary/10"
                            >
                              <span className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-orange-100">
                                <svg className="h-4 w-4 text-orange-500" viewBox="0 0 20 20" fill="currentColor">
                                  <path d="M10 0C4.478 0 0 4.478 0 10s4.478 10 10 10 10-4.478 10-10S15.522 0 10 0zm5.896 10.52a1.3 1.3 0 01-.384.922 3.33 3.33 0 01.064.62c0 3.152-3.67 5.71-8.194 5.71-4.524 0-8.194-2.558-8.194-5.71 0-.213.023-.422.066-.624a1.302 1.302 0 11.318-2.437 6.367 6.367 0 013.448-1.728l.584-2.746a.34.34 0 01.405-.258l1.938.406a.912.912 0 111.8.352l-1.76-.37-.52 2.44c1.302.063 2.492.424 3.448 1.006a1.302 1.302 0 11.286 2.437z" />
                                </svg>
                                {redditFeedHasUnseen && !redditFeedLoading && (
                                  <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-green-500 ring-2 ring-theme-surface" />
                                )}
                              </span>
                              <div className="flex-1">
                                <p className="flex items-center gap-1.5 text-sm font-medium text-theme-fg">
                                  Reddit Feed
                                  {redditFeedLoading && (
                                    <span className="h-3 w-3 animate-spin rounded-full border-2 border-orange-400 border-t-transparent" />
                                  )}
                                </p>
                                <p className="text-[11px] text-theme-fg-muted">
                                  {redditFeedLoading
                                    ? "Finding posts in the background…"
                                    : redditFeedHasUnseen
                                    ? "New posts ready to review."
                                    : "Find trending posts for content ideas."}
                                </p>
                              </div>
                            </button>
                          )}

                          {workingTools.map(({ id, config }) => (
                            <button
                              key={id}
                              type="button"
                              onClick={() => {
                                // Plan gate first: a chip the user's plan doesn't
                                // include must not pin a model or open its panel.
                                if (interceptGatedTool?.(id)) { closePlusMenu(); return; }
                                // Chip → model is data-driven (CHIP_PREFERRED_MODEL). A chip with
                                // no preferred model only needs to get us off a Perplexity model,
                                // which is chip-exclusive and would otherwise leak into the turn.
                                const preferred = modelForChip(id);
                                // onModelChange owns chip sync for chip-exclusive models, so
                                // calling onChipSelect for those too would toggle the chip back
                                // off. That also means Perplexity chips are never toggled off
                                // from this menu (use the chip's X) — deliberately unchanged.
                                const syncedByModelChange =
                                  preferred !== undefined &&
                                  MODEL_EXCLUSIVE_CHIP[preferred.model] !== undefined;
                                // For every other chip onChipSelect is a toggle, so clicking the
                                // active one turns it off. Drop the model it pinned in that case,
                                // or the user leaves Blog mode while still sending Sonnet 5.
                                const togglingOff = !syncedByModelChange && activeChipId === id;
                                if (onModelChange) {
                                  if (togglingOff) {
                                    if (preferred && selectedModel?.model === preferred.model) {
                                      onModelChange(SONNET_5_MODEL);
                                    }
                                  } else if (preferred) {
                                    onModelChange(preferred);
                                  } else if (selectedModel?.provider === "perplexity") {
                                    onModelChange(SONNET_5_MODEL);
                                  }
                                }
                                if (!syncedByModelChange) onChipSelect?.(id);
                                closePlusMenu();
                              }}
                              className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-theme-secondary/10"
                            >
                              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl ${config.iconBg}`}>
                                {config.icon}
                              </span>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-theme-fg">{config.label}</p>
                                <p className="text-[11px] text-theme-fg-muted">{config.description}</p>
                              </div>
                            </button>
                          ))}
                        </>
                      )}

                      <div className="pb-2" />
                    </>
                  )}
                </div>,
                document.body
              )}
            </div>
            )}
            {/* Model / provider selector pill */}
            {onModelChange && selectedModel && (
              <div className="relative ml-1" ref={modelMenuRef}>
                <button
                  type="button"
                  onClick={() => setShowModelMenu((prev) => !prev)}
                  className="flex h-7 items-center gap-1 rounded-full border border-theme-border bg-theme-surface px-2.5 text-[11px] text-theme-fg-muted transition-colors hover:bg-theme-secondary/10 focus:outline-none"
                  title="Select model"
                >
                  <span className="opacity-60">{selectedModel.providerLabel ?? selectedModel.provider}</span>
                  <span className="opacity-30 mx-0.5">·</span>
                  <span>{selectedModel.label}</span>
                  <svg className="h-3 w-3 opacity-50 ml-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showModelMenu && modelMenuAnchorRect && createPortal(
                  <div
                    ref={modelPortalRef}
                    className="fixed z-[9999] min-w-[220px] overflow-y-auto rounded-lg border border-theme-border bg-theme-surface py-1.5 shadow-lg"
                    style={{
                      bottom: `${window.innerHeight - modelMenuAnchorRect.top + 6}px`,
                      left: `${modelMenuAnchorRect.left}px`,
                      // This menu always opens upward (anchored by `bottom` above), so its
                      // available room is whatever sits above the composer, not the full
                      // viewport. Without a cap the list — now 12 entries, not the ~8 this was
                      // built for — grows past the top of the screen with no way to scroll to
                      // it, hiding the first entries (Sonnet 4.6 among them) entirely.
                      maxHeight: `${Math.max(modelMenuAnchorRect.top - 16, 120)}px`,
                    }}
                  >
                    {CHAT_MODEL_OPTIONS.map((opt) => {
                      const isActive =
                        opt.provider === selectedModel.provider &&
                        opt.model === selectedModel.model;
                      return (
                        <button
                          key={`${opt.provider}/${opt.model}`}
                          type="button"
                          onClick={() => {
                            onModelChange(opt);
                            setShowModelMenu(false);
                          }}
                          className={`flex w-full items-center justify-between px-4 py-2 text-left text-sm transition-colors hover:bg-theme-background ${isActive ? "font-medium text-theme-primary" : "text-theme-fg"}`}
                        >
                          <span>
                            <span className="opacity-60">{opt.providerLabel ?? opt.provider}</span>
                            <span className="mx-1 opacity-30">·</span>
                            {opt.label}
                            {(opt.model === recommendedModel || opt.hint) && (
                              <span className="ml-2 inline-block align-middle rounded border border-current px-1 py-px text-[10px] uppercase leading-none tracking-wide opacity-50">
                                {opt.model === recommendedModel ? "recommended" : opt.hint}
                              </span>
                            )}
                          </span>
                          {isActive && (
                            <svg className="h-4 w-4 shrink-0 text-theme-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </button>
                      );
                    })}
                  </div>,
                  document.body
                )}
              </div>
            )}
          </div>
          {isLoading && onStop ? (
            <button
              type="button"
              onClick={onStop}
              className="absolute bottom-2 right-2.5 flex h-9 w-9 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-theme-primary/30 focus:ring-offset-0 sm:bottom-2.5 sm:right-3 bg-theme-primary text-white shadow-sm transition-opacity duration-200 hover:opacity-90"
              title="Stop generating"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <rect x="5" y="5" width="14" height="14" rx="2" />
              </svg>
            </button>
          ) : isRecording ? (
            <button
              type="button"
              onClick={stopRecording}
              className="absolute bottom-2 right-2.5 flex h-9 w-9 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-red-400/30 focus:ring-offset-0 sm:bottom-2.5 sm:right-3 bg-red-500 text-white shadow-sm animate-pulse"
              title="Stop recording"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <rect x="5" y="5" width="14" height="14" rx="2" />
              </svg>
            </button>
          ) : isTranscribing ? (
            <button
              type="button"
              disabled
              className="absolute bottom-2 right-2.5 flex h-9 w-9 items-center justify-center rounded-full sm:bottom-2.5 sm:right-3 bg-theme-primary/40 text-white shadow-sm cursor-not-allowed"
              title="Processing audio…"
            >
              <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            </button>
          ) : (
            <div className="absolute bottom-2 right-2.5 flex items-center gap-1 sm:bottom-2.5 sm:right-3">
              <button
                type="button"
                onClick={startRecording}
                className="flex h-9 w-9 items-center justify-center rounded-full text-theme-fg-muted hover:bg-theme-secondary/10 hover:text-theme-fg focus:outline-none focus:ring-2 focus:ring-theme-primary/30 focus:ring-offset-0 transition-colors duration-200"
                title="Speak your message"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              </button>
              <button
                type="submit"
                disabled={(!value.trim() && selectedImages.length === 0) || isLoading}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-theme-primary text-white shadow-sm transition-opacity duration-200 hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-theme-primary/30 focus:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-45"
                title="Send message"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                </svg>
              </button>
            </div>
          )}
          </div>
        </form>

        {isSafetyReviewModel(selectedModel?.model) && (
          <p className="mt-2 text-center text-[11px] text-theme-fg-muted sm:text-xs">{SAFETY_REVIEW_NOTICE}</p>
        )}
        {isHighTokenUsageModel(selectedModel?.model) &&
          selectedModel?.model !== dismissedHighUsageModel && (
            <p className="mt-2 flex items-center justify-center gap-1.5 text-center text-[11px] text-theme-fg-muted sm:text-xs">
              <span>{selectedModel?.label} uses more tokens per reply than standard models.</span>
              <button
                type="button"
                onClick={() => setDismissedHighUsageModel(selectedModel?.model ?? null)}
                className="shrink-0 rounded-full p-0.5 text-theme-fg-muted transition-colors hover:bg-theme-secondary/10 hover:text-theme-fg"
                aria-label="Dismiss notice"
                title="Dismiss"
              >
                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </p>
          )}
        <p className="mt-2 text-center text-[11px] text-theme-fg-muted sm:text-xs">
          AI can make mistakes. Double-check responses before use.
        </p>
      </div>
    </div>
  );
};
